/**
 * `ClientsList` — the agency-side Clients page (ADR-0237 §6).
 *
 * Lists every (active | suspended) link row attached to the agency,
 * with per-row Switch into + Remove access. The server already
 * refuses the surrounding routes for non-agency sessions, so the
 * page itself gates on the agency feature + home-session invariants
 * so the empty / disabled copy is honest.
 */

import {
  ArrowRightLeft,
  Clock,
  Mail,
  MoreHorizontal,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ClientListItemWire } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import { useAuth } from '../../app/auth-context.js';
import {
  actAsClient,
  cancelLinkRequest,
  cancelPendingClient,
  listClients,
  listLinkRequests,
  resendPendingInvite,
  revokeAgencyLink,
  type LinkRequestWire,
} from '../../api/agency.js';
import { useC } from '../../lib/theme.js';
import {
  Button,
  Card,
  ConfirmDialog,
  NumLabel,
  Pill,
  PopoverMenu,
  PopoverMenuItem,
} from '../../components/atoms/index.js';
import { useIsEmbedded } from '../agency-portal/portal-shell-context.js';
import { AddClientDialog } from './AddClientDialog.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; clients: readonly ClientListItemWire[] }
  | { kind: 'error'; message: string };

export function ClientsList(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useAuth();
  const { features, acting } = useAgency();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [addOpen, setAddOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<ClientListItemWire | null>(null);
  const [confirmCancelPending, setConfirmCancelPending] = useState<ClientListItemWire | null>(null);
  const [confirmCancelRequest, setConfirmCancelRequest] = useState<LinkRequestWire | null>(null);
  const [pending, setPending] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  // ADR-0240 Portal v3 — per-row in-flight state for Cancel +
  // Resend. Keyed on `client_account_id` so each row shows its own
  // "Resending…" / "Cancelling…" indicator without blocking the
  // others.
  const [rowAction, setRowAction] = useState<{ id: string; kind: 'cancel' | 'resend' } | null>(null);
  // QA fix 2026-10-08 — pending outbound link requests. Fetched
  // alongside the clients list so the agency sees asks awaiting an
  // owner approval. Advisory only on this page; the server
  // enforces expiry.
  const [pendingRequests, setPendingRequests] = useState<readonly LinkRequestWire[]>([]);

  const user = auth.state.kind === 'authenticated' ? auth.state.user : null;

  const refresh = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const [cr, lr] = await Promise.all([
      listClients(http),
      listLinkRequests(http),
    ]);
    if (cr.kind === 'ok') {
      setState({ kind: 'ok', clients: cr.clients });
    } else if (cr.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Agency access is not available on this account.' });
    } else {
      setState({ kind: 'error', message: cr.message });
    }
    if (lr.kind === 'ok') {
      setPendingRequests(lr.requests);
    } else {
      setPendingRequests([]);
    }
  }, [http]);

  useEffect(() => {
    if (features === null) return; // wait for features to resolve
    if (features.agency !== true) return; // disabled copy below
    if (acting !== null) return; // acting sessions can't list clients
    void refresh();
  }, [features, acting, refresh]);

  // Guardrails on the surrounding state ------------------------------
  if (features === null) {
    return (
      <ClientsShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </ClientsShell>
    );
  }
  if (acting !== null) {
    return (
      <ClientsShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Return to your home account to manage clients.
          </div>
        </Card>
      </ClientsShell>
    );
  }

  if (features.agency !== true) {
    return (
      <ClientsShell>
        <Card className="p-5 sm:p-6">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              lineHeight: 1.5,
              marginBottom: 8,
            }}
          >
            Clients is an agency add-on.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Contact Sill to enable it on this account. The feature stays off until a Sill staff member turns it on for you.
          </div>
        </Card>
      </ClientsShell>
    );
  }
  const doSwitch = async (c: ClientListItemWire): Promise<void> => {
    if (http === null || user === null || switching !== null) return;
    setSwitching(c.client_account_id);
    setRowError(null);
    const r = await actAsClient(http, c.client_account_id);
    if (r.kind === 'ok') {
      // ADR-0237 — refresh the AuthProvider so the next /me reads the
      // server-authoritative acting_context (home + acting account
      // names + ids + roles). No client-side persistence; the banner
      // and switcher pick the acting state up directly from the
      // refreshed User on reload.
      try {
        await auth.refresh();
      } catch {
        /* refresh race → the reload below falls through cleanly. */
      }
      // Land on the client's own dashboard (its sites) — the Agency
      // Portal is unavailable while acting (founder 2026-10-11).
      if (typeof window !== 'undefined') window.location.assign('/?view=sites');
      return;
    }
    setSwitching(null);
    if (r.kind === 'not_found') {
      setRowError('That client is no longer linked. Refresh to update the list.');
    } else if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
    } else {
      setRowError(r.message);
    }
  };

  const doCancelPending = async (): Promise<void> => {
    if (http === null || confirmCancelPending === null || rowAction !== null) return;
    const client = confirmCancelPending;
    setRowAction({ id: client.client_account_id, kind: 'cancel' });
    setRowError(null);
    const r = await cancelPendingClient(http, client.client_account_id);
    setRowAction(null);
    setConfirmCancelPending(null);
    if (r.kind === 'ok') {
      void refresh();
      return;
    }
    if (r.kind === 'already_claimed') {
      setRowError('That client already claimed their account. Refresh the list.');
      void refresh();
      return;
    }
    if (r.kind === 'not_pending' || r.kind === 'not_found') {
      setRowError('That pending client no longer exists. Refresh the list.');
      void refresh();
      return;
    }
    if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
      return;
    }
    setRowError(r.message);
  };

  const doResendInvite = async (client: ClientListItemWire): Promise<void> => {
    if (http === null || rowAction !== null) return;
    setRowAction({ id: client.client_account_id, kind: 'resend' });
    setRowError(null);
    const r = await resendPendingInvite(http, client.client_account_id);
    setRowAction(null);
    if (r.kind === 'ok') {
      // Show a quick inline confirmation via rowError's green cousin — the
      // simplest reuse here is a non-coral message slot; we lean on the
      // existing error row with "sent" copy so the user has feedback.
      setRowError('Invite resent.');
      return;
    }
    if (r.kind === 'already_claimed') {
      setRowError('That client already claimed their account.');
      void refresh();
      return;
    }
    if (r.kind === 'not_pending' || r.kind === 'not_found') {
      setRowError('That pending client no longer exists.');
      void refresh();
      return;
    }
    if (r.kind === 'rate_limited') {
      setRowError(r.message);
      return;
    }
    if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
      return;
    }
    setRowError(r.message);
  };

  const doCancelLinkRequest = async (): Promise<void> => {
    if (http === null || confirmCancelRequest === null || rowAction !== null) return;
    const req = confirmCancelRequest;
    setRowAction({ id: req.request_id, kind: 'cancel' });
    setRowError(null);
    const r = await cancelLinkRequest(http, req.request_id);
    setRowAction(null);
    setConfirmCancelRequest(null);
    if (r.kind === 'ok') {
      void refresh();
      return;
    }
    if (r.kind === 'already_approved' || r.kind === 'already_rejected' || r.kind === 'not_found') {
      setRowError('That request is no longer pending. Refresh the list.');
      void refresh();
      return;
    }
    if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
      return;
    }
    setRowError(r.message);
  };

  const doRevoke = async (): Promise<void> => {
    if (http === null || confirmRevoke === null || pending) return;
    setPending(true);
    setRowError(null);
    const r = await revokeAgencyLink(http, confirmRevoke.link_id);
    setPending(false);
    if (r.kind === 'ok') {
      setConfirmRevoke(null);
      void refresh();
      return;
    }
    if (r.kind === 'not_found') {
      setRowError('That link no longer exists.');
      setConfirmRevoke(null);
      void refresh();
      return;
    }
    if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
      return;
    }
    setRowError(r.message);
  };

  return (
    <ClientsShell>
      <div className="flex items-center justify-between mb-6">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          {state.kind === 'ok'
            ? (() => {
                // QA fix 2026-10-08 — pending_owner clients haven't been
                // claimed yet; they are visible on the list but should NOT
                // bump the "linked" count (nothing is actually linked
                // until the owner clicks the invite). Count only clients
                // whose owner has claimed.
                const claimed = state.clients.filter(
                  (c) => c.owner_claim_status !== 'pending_owner',
                ).length;
                const pending = state.clients.length - claimed;
                return pending > 0
                  ? `${claimed} linked · ${pending} awaiting owner`
                  : `${claimed} linked`;
              })()
            : ''}
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-2"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 14px',
            border: 'none',
            borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          <Plus size={13} /> Add client
        </button>
      </div>

      {state.kind === 'loading' ? (
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading clients…
          </div>
        </Card>
      ) : null}

      {state.kind === 'error' ? (
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>
            {state.message}
          </div>
        </Card>
      ) : null}

      {state.kind === 'ok' && state.clients.length === 0 ? (
        <Card className="p-5 sm:p-6">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              marginBottom: 6,
            }}
          >
            No linked clients yet.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 16 }}>
            Create a new client account or ask an existing account owner for access.
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 14px',
              border: 'none',
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            <Plus size={13} /> Add client
          </button>
        </Card>
      ) : null}

      {state.kind === 'ok' && state.clients.length > 0 ? (
        <Card className="p-0" style={{ overflowX: 'auto' }}>
          <div className="hidden md:grid" style={rowStyle(C)}>
            <HeadCell>Name</HeadCell>
            <HeadCell>Sites</HeadCell>
            <HeadCell>Status</HeadCell>
            <HeadCell>Access</HeadCell>
            <HeadCell>Added</HeadCell>
            <HeadCell><span style={{ display: 'block', textAlign: 'right' }}>Actions</span></HeadCell>
          </div>
          {state.clients.map((c) => (
            <ClientRow
              key={c.link_id}
              client={c}
              onSwitch={() => void doSwitch(c)}
              onRevoke={() => {
                setConfirmRevoke(c);
                setRowError(null);
              }}
              onCancelPending={() => {
                setConfirmCancelPending(c);
                setRowError(null);
              }}
              onResendInvite={() => void doResendInvite(c)}
              switching={switching === c.client_account_id}
              pendingRowAction={
                rowAction !== null && rowAction.id === c.client_account_id
                  ? rowAction.kind
                  : null
              }
            />
          ))}
        </Card>
      ) : null}

      {rowError !== null ? (
        <div
          className="mt-4"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}
        >
          {rowError}
        </div>
      ) : null}

      {/* QA fix 2026-10-08 — pending outbound link requests. Shown
          beneath the clients table so the agency sees asks that have
          not yet been approved by the client owner. */}
      {pendingRequests.length > 0 ? (
        <div className="mt-8">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Pending requests · {pendingRequests.length}
          </div>
          <Card className="p-0 overflow-hidden">
            {pendingRequests.map((req) => (
              <div
                key={req.request_id}
                className="flex items-center gap-3 px-4 py-3"
                style={{
                  borderBottom: `1px solid ${C.borderSoft}`,
                  background: 'transparent',
                }}
              >
                <Clock size={13} color={C.amberFg} />
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {req.target_email ?? req.target_domain ?? '(unknown target)'}
                  </div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.muted,
                      marginTop: 2,
                    }}
                  >
                    {req.requested_role} · waiting for the owner to approve · expires {formatDate(req.expires_at)}
                  </div>
                </div>
                <Pill tone="amber">Waiting</Pill>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmCancelRequest(req);
                    setRowError(null);
                  }}
                  disabled={rowAction !== null && rowAction.id === req.request_id}
                  className="flex items-center gap-1"
                  style={{
                    background: 'transparent',
                    color: C.coralFg,
                    fontFamily: sansFont,
                    fontSize: 12,
                    padding: '6px 10px',
                    border: `1px solid ${C.coralFg}`,
                    borderRadius: 2,
                    cursor: rowAction !== null && rowAction.id === req.request_id ? 'default' : 'pointer',
                    opacity: rowAction !== null && rowAction.id === req.request_id ? 0.6 : 1,
                  }}
                  aria-label={`Cancel link request to ${req.target_email ?? req.target_domain ?? 'target'}`}
                >
                  <Trash2 size={11} />{' '}
                  {rowAction !== null && rowAction.id === req.request_id && rowAction.kind === 'cancel'
                    ? 'Cancelling…'
                    : 'Cancel'}
                </button>
              </div>
            ))}
          </Card>
        </div>
      ) : null}

      <AddClientDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          void refresh();
        }}
      />

      <ConfirmDialog
        open={confirmRevoke !== null}
        title={confirmRevoke !== null ? `Remove access to ${confirmRevoke.client_name}?` : 'Remove access?'}
        body={
          <>
            The link is revoked immediately — your agency can no longer act inside this client on the next request. The link row is kept for the audit trail; the client owner can re-grant access any time from their Account settings.
          </>
        }
        confirmLabel={pending ? 'Removing…' : 'Remove access'}
        confirmTone="coral"
        onConfirm={() => void doRevoke()}
        onClose={() => {
          if (pending) return;
          setConfirmRevoke(null);
          setRowError(null);
        }}
      />

      <ConfirmDialog
        open={confirmCancelPending !== null}
        title={
          confirmCancelPending !== null
            ? `Cancel pending account for ${confirmCancelPending.client_name}?`
            : 'Cancel pending account?'
        }
        body={
          <>
            The invite you sent will stop working, and this pending account will no longer be reachable from the Clients list. If you created the account with the wrong email, use Cancel and start again with the correct address.
          </>
        }
        confirmLabel={rowAction?.kind === 'cancel' ? 'Cancelling…' : 'Cancel account'}
        confirmTone="coral"
        onConfirm={() => void doCancelPending()}
        onClose={() => {
          if (rowAction?.kind === 'cancel') return;
          setConfirmCancelPending(null);
          setRowError(null);
        }}
      />

      <ConfirmDialog
        open={confirmCancelRequest !== null}
        title="Cancel this access request?"
        body={
          <>
            The approval link in the owner's inbox will stop working. The owner will no longer be able to approve this request. You can send a fresh one from Add client.
          </>
        }
        confirmLabel={rowAction?.kind === 'cancel' ? 'Cancelling…' : 'Cancel request'}
        confirmTone="coral"
        onConfirm={() => void doCancelLinkRequest()}
        onClose={() => {
          if (rowAction?.kind === 'cancel') return;
          setConfirmCancelRequest(null);
          setRowError(null);
        }}
      />
    </ClientsShell>
  );
}

function ClientsShell({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  const embedded = useIsEmbedded();
  if (embedded) {
    // AgencyPortal renders the kicker, H1 and container itself (one
    // header per section). Avoid double-rendering.
    return <>{children}</>;
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>Agency</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Clients.
        </h1>
      </div>
      {children}
    </div>
  );
}

/**
 * One shared grid template for the Clients table header and rows (same
 * approach as `ReportsTable.GRID_TEMPLATE`). Sized so the whole row
 * fits the portal content column (~860 px at a 1440 px window) without
 * horizontal scroll or wrapping:
 *   - Name takes the fluid space and stacks the primary domain under it;
 *   - Access stacks "how added" over the granted role;
 *   - Actions fits the widest primary ("Resend invite") + the ⋯ trigger.
 */
const GRID_TEMPLATE = 'minmax(240px, 1fr) 44px 116px 140px 84px 152px';

function rowStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    gridTemplateColumns: GRID_TEMPLATE,
    gap: 10,
    padding: '10px 12px',
    borderBottom: `1px solid ${C.borderSoft}`,
    alignItems: 'center',
    background: C.surface2,
    whiteSpace: 'nowrap',
  };
}

function HeadCell({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}

interface ClientRowProps {
  client: ClientListItemWire;
  onSwitch: () => void;
  onRevoke: () => void;
  /** ADR-0240 Portal v3 — cancel a pending-owner account whose
   *  owner never clicked the invite. Only wired when the row is
   *  pending-owner + agency-created. */
  onCancelPending: () => void;
  /** ADR-0240 Portal v3 — resend the invite with a fresh magic link
   *  for the pending-owner account. Burns one daily slot. */
  onResendInvite: () => void;
  switching: boolean;
  /** Set while a resend or cancel is in flight on THIS row. */
  pendingRowAction: 'cancel' | 'resend' | null;
}

function ClientRow({
  client,
  onSwitch,
  onRevoke,
  onCancelPending,
  onResendInvite,
  switching,
  pendingRowAction,
}: ClientRowProps): JSX.Element {
  const C = useC();
  const C2 = useC();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Compose the status label — "Awaiting owner" supersedes the raw
  // link_status when the client account is still in pending_owner.
  const status_label =
    client.owner_claim_status === 'pending_owner'
      ? 'Awaiting owner'
      : client.link_status === 'active'
        ? 'Active'
        : client.link_status === 'suspended'
          ? 'Suspended'
          : 'Revoked';
  const status_tone: 'amber' | 'lime' | 'coral' =
    client.owner_claim_status === 'pending_owner'
      ? 'amber'
      : client.link_status === 'active'
        ? 'lime'
        : client.link_status === 'suspended'
          ? 'amber'
          : 'coral';
  // QA fix 2026-10-08 — pending_owner clients can't be switched into:
  // the agency HAS an active link row but the client has no owner user,
  // and the dashboard surfaces inside the client expect an owner_claimed
  // account. Disable the Switch button and surface the status via the
  // Awaiting-owner pill below.
  const can_switch =
    client.link_status === 'active'
    && client.owner_claim_status !== 'pending_owner';

  const provenance_label =
    client.provenance === 'agency_created' ? 'Created by you' : 'Approved by client';

  const is_pending_owner =
    client.owner_claim_status === 'pending_owner'
    && client.provenance === 'agency_created';

  const primary_label = is_pending_owner ? 'Resend invite' : 'Switch';
  const primary_disabled = pendingRowAction !== null
    || switching
    || (!is_pending_owner && !can_switch);
  const primary_busy_label = is_pending_owner
    ? pendingRowAction === 'resend' ? 'Resending…' : null
    : switching ? 'Switching…' : null;

  const onPrimary = (): void => {
    if (is_pending_owner) onResendInvite();
    else onSwitch();
  };

  return (
    <>
      {/* Desktop row */}
      <div className="hidden md:grid" style={{ ...rowStyle(C), background: 'transparent' }}>
        {/* Agency gaps fix 2026-10-11 — the name column stacks name
            + primary domain, so the row never truncates "Northwind …"
            when there is only a sliver of horizontal space. */}
        <div
          style={{
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <div
            title={client.client_name} /* title-ok: full value of the ellipsised name */
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {client.client_name}
          </div>
          <div
            title={client.primary_domain ?? undefined} /* title-ok: full value of the ellipsised domain */
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {client.primary_domain ?? '—'}
          </div>
        </div>
        <div style={{ fontFamily: monoFont, fontSize: 12, color: C.body }}>
          {client.sites_count}
        </div>
        <div>
          <Pill tone={status_tone}>{status_label}</Pill>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.body }}>
            {provenance_label}
          </div>
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2 }}>
            {client.granted_role}
          </div>
        </div>
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {formatDate(client.created_at)}
        </div>
        <div className="flex items-center gap-1 justify-end" style={{ whiteSpace: 'nowrap' }}>
          <Button
            variant="secondary"
            size="sm"
            icon={is_pending_owner ? Mail : ArrowRightLeft}
            disabled={primary_disabled}
            onClick={onPrimary}
          >
            {primary_busy_label ?? primary_label}
          </Button>
          <button
            ref={menuTriggerRef}
            type="button"
            data-testid={`clients-row-menu-${client.link_id}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={`More actions for ${client.client_name}`}
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '4px 6px',
              color: C.text,
              cursor: 'pointer',
              lineHeight: 0,
            }}
          >
            <MoreHorizontal size={14} />
          </button>
          <PopoverMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorRef={menuTriggerRef}
            testid={`clients-row-menu-popover-${client.link_id}`}
          >
            {is_pending_owner ? (
              <>
                <PopoverMenuItem
                  label="Resend invite"
                  icon={Mail}
                  disabled={pendingRowAction !== null}
                  onClick={() => {
                    setMenuOpen(false);
                    onResendInvite();
                  }}
                />
                <PopoverMenuItem
                  label="Cancel account"
                  icon={X}
                  tone="danger"
                  disabled={pendingRowAction !== null}
                  onClick={() => {
                    setMenuOpen(false);
                    onCancelPending();
                  }}
                />
              </>
            ) : (
              <>
                <PopoverMenuItem
                  label="Switch into client"
                  icon={ArrowRightLeft}
                  disabled={!can_switch || switching}
                  onClick={() => {
                    setMenuOpen(false);
                    onSwitch();
                  }}
                />
                <PopoverMenuItem
                  label="Remove access"
                  icon={Trash2}
                  tone="danger"
                  onClick={() => {
                    setMenuOpen(false);
                    onRevoke();
                  }}
                />
              </>
            )}
          </PopoverMenu>
        </div>
      </div>

      {/* Mobile card */}
      <div
        className="md:hidden"
        style={{ padding: '12px 14px', borderBottom: `1px solid ${C2.borderSoft}` }}
      >
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C2.text,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {client.client_name}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C2.muted,
            marginTop: 2,
          }}
        >
          {client.primary_domain ?? '—'} · {client.sites_count} site{client.sites_count === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 8 }}>
          <Pill tone={status_tone}>{status_label}</Pill>
          <span style={{ fontFamily: sansFont, fontSize: 11, color: C2.muted }}>
            {provenance_label}
          </span>
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C2.muted }}>
            <Clock size={10} style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {formatDate(client.created_at)}
          </span>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            disabled={!can_switch || switching}
            onClick={onSwitch}
            className="flex items-center gap-1"
            style={{
              background: 'transparent',
              color: can_switch ? C2.text : C2.dim,
              fontFamily: sansFont,
              fontSize: 13,
              padding: '8px 12px',
              border: `1px solid ${C2.border}`,
              borderRadius: 2,
              cursor: can_switch && !switching ? 'pointer' : 'not-allowed',
            }}
          >
            <ArrowRightLeft size={13} /> Switch
          </button>
          <button
            type="button"
            onClick={onRevoke}
            className="flex items-center gap-1"
            style={{
              background: 'transparent',
              color: C2.coralFg,
              fontFamily: sansFont,
              fontSize: 13,
              padding: '8px 12px',
              border: `1px solid ${C2.coralFg}`,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={13} /> Remove
          </button>
        </div>
      </div>
    </>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function humanizeForbidden(reason: string): string {
  switch (reason) {
    case 'agency_feature_disabled':
      return 'The agency feature was turned off on your account.';
    case 'role_insufficient':
      return 'Only owners and admins may switch into a client.';
    case 'acting_session_denied':
      return 'Return to your home account first.';
    default:
      return 'Access denied.';
  }
}
